import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import ReactMarkdown from "react-markdown";
import {
  Lightning, Copy, Check, SpinnerGap, Notebook,
  DownloadSimple, ArrowsClockwise,
} from "@phosphor-icons/react";

const CHANNELS = [
  { key: "blog", label: "Blog" },
  { key: "linkedin", label: "LinkedIn" },
  { key: "x", label: "X / Twitter" },
  { key: "newsletter", label: "Newsletter" },
  { key: "instagram", label: "Instagram" },
  { key: "facebook", label: "Facebook" },
  { key: "youtube", label: "YouTube Script" },
];

const OBJECTIVES = [
  "Educate and drive engagement",
  "Generate leads",
  "Build authority and thought leadership",
  "Promote a product or service",
  "Start a conversation",
  "Drive traffic to a landing page",
];

export function StudioCreateTab({ api, availableModels, defaultAudience, defaultTone, defaultModel, onPackageCreated }) {
  const [idea, setIdea] = useState("");
  const [audience, setAudience] = useState(defaultAudience);
  const [objective, setObjective] = useState(OBJECTIVES[0]);
  const [tone, setTone] = useState(defaultTone);
  const [wordCount, setWordCount] = useState(1200);
  const [selectedChannels, setSelectedChannels] = useState(["blog", "linkedin", "x", "newsletter"]);
  const [sourceNotes, setSourceNotes] = useState("");
  const [aiModel, setAiModel] = useState(defaultModel);
  const [generating, setGenerating] = useState(false);
  const [packageData, setPackageData] = useState(null);
  const [copied, setCopied] = useState(false);

  const toggleChannel = (key) => setSelectedChannels(prev => prev.includes(key) ? prev.filter(c => c !== key) : [...prev, key]);

  const handleGenerate = async () => {
    if (!idea.trim() || idea.trim().length < 5) { toast.error("Idea must be at least 5 characters"); return; }
    if (!aiModel) { toast.error("Select an AI model"); return; }
    if (selectedChannels.length === 0) { toast.error("Select at least one channel"); return; }
    setGenerating(true); setPackageData(null);
    try {
      const resp = await api.post("/content-studio/idea-to-post", {
        idea: idea.trim(), target_audience: audience.trim() || "General business audience",
        objective, tone: tone.trim() || "Clear, practical, confident",
        channels: selectedChannels, desired_word_count: wordCount,
        source_notes: sourceNotes.trim(), ai_model: aiModel,
      });
      setPackageData(resp.data);
      onPackageCreated?.(resp.data);
      toast.success("Content package generated!");
    } catch (err) { toast.error(err.response?.data?.detail || "Generation failed"); }
    finally { setGenerating(false); }
  };

  const copyAll = async (text) => {
    try { await navigator.clipboard.writeText(text); } catch {
      const ta = document.createElement("textarea"); ta.value = text; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
    }
    setCopied(true); setTimeout(() => setCopied(false), 2000); toast.success("Copied!");
  };

  const downloadMd = () => {
    if (!packageData?.package_markdown) return;
    const blob = new Blob([packageData.package_markdown], { type: "text/markdown" });
    const url = URL.createObjectURL(blob); const a = document.createElement("a");
    a.href = url; a.download = `content-package-${packageData.package_id || "export"}.md`; a.click(); URL.revokeObjectURL(url);
  };

  const Label = ({ children, required }) => (
    <label className="block text-[10px] font-bold uppercase tracking-[0.15em] text-[var(--text-muted)] mb-1.5">
      {children}{required && <span className="text-red-400 ml-0.5">*</span>}
    </label>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      <div className="lg:col-span-3 space-y-5">
        <div>
          <Label required>Seed Idea</Label>
          <Textarea value={idea} onChange={e => setIdea(e.target.value.slice(0, 500))} placeholder='"Why 80% of content strategies fail in the first 90 days"' rows={3} className="bg-[var(--bg-card)] border-[var(--border-strong)] text-sm resize-none" data-testid="studio-idea-input" />
          <p className="text-[10px] text-[var(--text-muted)] mt-1 text-right">{idea.length}/500</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><Label>Target Audience</Label><Input value={audience} onChange={e => setAudience(e.target.value.slice(0, 150))} placeholder="e.g., B2B SaaS founders" className="bg-[var(--bg-card)] border-[var(--border-strong)] text-sm" data-testid="studio-audience-input" /></div>
          <div><Label>Objective</Label><Select value={objective} onValueChange={setObjective}><SelectTrigger className="bg-[var(--bg-card)] border-[var(--border-strong)] text-sm" data-testid="studio-objective-select"><SelectValue /></SelectTrigger><SelectContent>{OBJECTIVES.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select></div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><Label>Tone</Label><Input value={tone} onChange={e => setTone(e.target.value.slice(0, 100))} placeholder="Clear, practical, confident" className="bg-[var(--bg-card)] border-[var(--border-strong)] text-sm" data-testid="studio-tone-input" /></div>
          <div><Label>Target Word Count</Label><Select value={String(wordCount)} onValueChange={v => setWordCount(Number(v))}><SelectTrigger className="bg-[var(--bg-card)] border-[var(--border-strong)] text-sm" data-testid="studio-wordcount-select"><SelectValue /></SelectTrigger><SelectContent>{[300,600,900,1200,1800,2400,3000].map(w => <SelectItem key={w} value={String(w)}>{w.toLocaleString()} words</SelectItem>)}</SelectContent></Select></div>
        </div>

        <div>
          <Label required>Distribution Channels</Label>
          <div className="flex flex-wrap gap-2" data-testid="studio-channels">
            {CHANNELS.map(ch => (
              <button key={ch.key} onClick={() => toggleChannel(ch.key)} data-testid={`studio-channel-${ch.key}`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${selectedChannels.includes(ch.key) ? "bg-[#FF4400]/10 border-[#FF4400]/40 text-[#FF4400]" : "bg-[var(--bg-card)] border-[var(--border)] text-[var(--text-secondary)] hover:border-[var(--text-muted)]"}`}>
                {ch.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label required>AI Model</Label>
          <Select value={aiModel} onValueChange={setAiModel}><SelectTrigger className="bg-[var(--bg-card)] border-[var(--border-strong)] text-sm" data-testid="studio-model-select"><SelectValue placeholder="Select model" /></SelectTrigger><SelectContent>{availableModels.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select>
        </div>

        <div>
          <Label>Source Notes (Optional)</Label>
          <Textarea value={sourceNotes} onChange={e => setSourceNotes(e.target.value.slice(0, 5000))} placeholder="Paste any research, stats, quotes, or context..." rows={4} className="bg-[var(--bg-card)] border-[var(--border-strong)] text-sm resize-y" data-testid="studio-source-notes" />
        </div>

        <Button onClick={handleGenerate} disabled={generating || idea.trim().length < 5 || !aiModel || selectedChannels.length === 0}
          className="w-full h-12 bg-[#FF4400] hover:bg-[#FF6633] text-white font-semibold rounded-xl transition-all" data-testid="studio-generate-btn">
          {generating ? <><SpinnerGap size={18} className="animate-spin mr-2" /> Generating Package...</> : <><Lightning size={18} weight="fill" className="mr-2" /> Generate Content Package</>}
        </Button>
      </div>

      {/* Preview */}
      <div className="lg:col-span-2">
        <div className="sticky top-20 space-y-3">
          <Label>Package Output</Label>
          <AnimatePresence mode="wait">
            {generating ? (
              <motion.div key="l" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-8 flex flex-col items-center gap-3 min-h-[300px] justify-center">
                <SpinnerGap size={40} className="animate-spin text-[#FF4400]" />
                <p className="text-xs text-[var(--text-muted)]">Building research brief, canonical draft, and channel variants...</p>
              </motion.div>
            ) : packageData ? (
              <motion.div key="r" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
                <div className="flex items-center justify-between bg-[var(--bg-card)] border border-[var(--border)] rounded-xl px-4 py-3">
                  <div className="flex items-center gap-2"><Notebook size={16} className="text-[#FF4400]" /><span className="text-xs font-mono text-[var(--text-muted)]">{packageData.package_id}</span></div>
                  {packageData.saved && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 font-semibold">Saved</span>}
                </div>
                <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-5 max-h-[500px] overflow-y-auto" data-testid="studio-package-output">
                  <div className="prose prose-sm max-w-none text-[var(--text-primary)] prose-headings:text-[var(--text-primary)] prose-strong:text-[var(--text-primary)] prose-p:text-[var(--text-secondary)] prose-li:text-[var(--text-secondary)]">
                    <ReactMarkdown>{packageData.package_markdown}</ReactMarkdown>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => copyAll(packageData.package_markdown)} variant="outline" className="flex-1 h-10 rounded-xl gap-1.5 text-xs" data-testid="studio-copy-btn">{copied ? <Check size={14} /> : <Copy size={14} />}{copied ? "Copied!" : "Copy All"}</Button>
                  <Button onClick={downloadMd} variant="outline" className="flex-1 h-10 rounded-xl gap-1.5 text-xs" data-testid="studio-download-btn"><DownloadSimple size={14} /> Download .md</Button>
                  <Button onClick={handleGenerate} variant="outline" className="h-10 px-3 rounded-xl" data-testid="studio-regenerate-btn"><ArrowsClockwise size={14} /></Button>
                </div>
              </motion.div>
            ) : (
              <motion.div key="e" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-8 flex flex-col items-center gap-3 min-h-[300px] justify-center text-center">
                <Lightning size={48} className="text-[var(--text-muted)] opacity-20" />
                <p className="text-xs text-[var(--text-muted)]">Your content package will appear here</p>
                <p className="text-[10px] text-[var(--text-muted)] max-w-[200px]">Research brief, full draft, channel variants, and syndication metadata</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
